/**
 * Figure d'une TRANSLATION (187 « L'image d'un sommet » ; 189 « La figure
 * image » ; 190 « Pourquoi ? ») : un
 * quadrilatère ou un triangle (la figure verte) et son image par une
 * translation (la figure orange), côte à côte, comme les fiches du prof (ex. 1
 * à 3 de l'attendu : « La figure orange est l'image de la figure verte par
 * une translation », RAMI et BELO) ; et les FLÈCHES de translation de 190
 * « La bonne propriété » (svgFleches).
 *
 * Figure EXACTE (décision prof 24/09) : l'image est la figure décalée d'un
 * vecteur ; les mesures écrites sur la figure de départ sont celles du dessin
 * (longueurs au dixième de cm, angles au degré). Les noms de l'image sont
 * donnés sommet par sommet : des primes (A → A') ou d'autres lettres dans un
 * autre ordre (RAMI → BELO : il faut lire la figure).
 *
 * data-figure : les points des deux figures (coordonnées en cm), pour que les
 * solveurs retrouvent eux-mêmes la translation et la correspondance.
 */
import { randInt, randomChoice, pickLettresSains, tex } from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const W = 360; const H = 240;
const f1 = (v) => v.toFixed(1);
const VERT = '#D9E8A8'; const ORANGE = '#F8C29B'; const TRAIT = '#334155';
const COULEUR_COTE = '#0369A1';
const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const norme = (u) => Math.hypot(u[0], u[1]);
const unitaire = (u) => { const L = norme(u) || 1; return [u[0] / L, u[1] / L]; };
const perp = (u) => [-u[1], u[0]];
const largeurTexte = (t) => [...String(t)].reduce((s, c) => s + (c === ' ' || c === ',' || c === '.' ? 3.6 : c === 'm' ? 10.8 : c === '°' ? 5.2 : 7.3), 0);

/** Longueur (cm) et angle intérieur (degrés) d'un polygone convexe. */
export const longueur = (P, Q) => norme(vec(P, Q));
export const angleInterieur = (poly, i) => {
    const n = poly.length; const S = poly[i];
    const a = unitaire(vec(S, poly[(i + n - 1) % n])); const b = unitaire(vec(S, poly[(i + 1) % n]));
    return (Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1]))) * 180) / Math.PI;
};

/**
 * Un quadrilatère convexe « lisible » en cm (sens direct) : côtés de 1,8 à
 * 4,5 cm, angles de 55° à 130°, aucun angle droit à 3° près sauf s'il est
 * voulu, mesures au dixième et au degré loin des pivots d'arrondi.
 */
export const quadrilatere = () => {
    for (let essai = 0; essai < 500; essai++) {
        const base = randInt(0, 89);
        const poly = [0, 1, 2, 3].map((i) => {
            const t = ((base + i * 90 + randInt(-22, 22)) * Math.PI) / 180; const r = 1.6 + Math.random() * 1.2;
            return [r * Math.cos(t), r * Math.sin(t)];
        });
        const cotes = poly.map((P, i) => longueur(P, poly[(i + 1) % 4]));
        const angles = poly.map((_, i) => angleInterieur(poly, i));
        const convexe = poly.every((P, i) => {
            const u = vec(P, poly[(i + 1) % 4]); const v = vec(poly[(i + 1) % 4], poly[(i + 2) % 4]);
            return u[0] * v[1] - u[1] * v[0] > 0;
        });
        if (!convexe) continue;
        if (cotes.some((c) => c < 1.8 || c > 4.5 || Math.abs(c * 10 - Math.floor(c * 10) - 0.5) < 0.1)) continue;
        if (angles.some((a) => a < 55 || a > 130 || Math.abs(a - Math.floor(a) - 0.5) < 0.1 || Math.abs(a - 90) < 3)) continue;
        // les mesures affichées (dixième, degré) toutes différentes : aucune ne se confond avec une autre
        const c10 = cotes.map((c) => Math.round(c * 10)); const a1 = angles.map((a) => Math.round(a));
        if (new Set(c10).size !== 4 || new Set(a1).size !== 4) continue;
        return { poly, cotes: c10, angles: a1 };
    }
    throw new Error('figTranslation : quadrilatère introuvable');
};

/**
 * La figure image décalée à droite ou à gauche, un peu plus haut ou plus bas :
 * vecteur en cm. L'écart entre les deux figures grandit avec leur largeur (au
 * moins 2,2 cm, 0,7 fois la largeur) : à 2,2 cm fixes, deux rectangles de 9 cm
 * se touchaient presque et leurs noms se collaient (« K'F », vu en PNG). Le
 * décalage vertical reste petit (un quart de la hauteur au plus) : plus grand,
 * il tassait toute la figure et les mesures d'angle se serraient.
 */
export const vecteurCoteACote = (poly) => {
    const xs = poly.map((p) => p[0]); const ys = poly.map((p) => p[1]);
    const w = Math.max(...xs) - Math.min(...xs); const h = Math.max(...ys) - Math.min(...ys);
    const sens = randomChoice([1, -1]);
    return [sens * (w + Math.max(2.2, 0.7 * w)), (Math.random() - 0.5) * h * 0.5];
};

/**
 * Le dessin. noms : les lettres de la figure de départ (dans l'ordre des
 * sommets) ; nomsImage : la lettre de l'image de chaque sommet (même ordre) ;
 * cotes : [{ i, texte }] (le côté [sommet i, sommet i + 1]) ; angles :
 * [{ i, texte }] ; droits : [i] (angle droit codé au sommet i) ; tout est
 * écrit sur la figure de départ. La figure peut avoir trois sommets.
 */
export const svgTranslation = ({ poly, noms, nomsImage, vecteur, cotes = [], angles = [], droits = [], aria }) => {
    const image = poly.map(([x, y]) => [x + vecteur[0], y + vecteur[1]]);
    const tous = [...poly, ...image];
    const xs = tous.map((p) => p[0]); const ys = tous.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs); const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const centre = (T) => [T.reduce((s, p) => s + p[0], 0) / T.length, T.reduce((s, p) => s + p[1], 0) / T.length];
    /**
     * Les sommets en pixels pour une marge donnée, et toutes les écritures
     * (longueurs, mesures d'angle, noms des sommets) avec leur boîte : si l'une
     * sort du cadre, la marge grandit (une longueur coupée au bord, « 3,5 c »,
     * vue en PNG).
     */
    const placer = (marge) => {
        const ech = Math.min((W - 2 * marge) / (xmax - xmin), (H - 2 * marge) / (ymax - ymin));
        const x0 = (W - (xmax - xmin) * ech) / 2; const y0 = (H - (ymax - ymin) * ech) / 2;
        const px = (p) => [x0 + (p[0] - xmin) * ech, y0 + (ymax - p[1]) * ech];
        const A = poly.map(px); const B = image.map(px);
        const G = centre(A); const G2 = centre(B);
        const ecritures = [];
        const longueurs = cotes.map(({ i, texte }) => {
            const P = A[i]; const Q = A[(i + 1) % A.length];
            const M = [(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2];
            let n = perp(unitaire(vec(P, Q))); if ((M[0] - G[0]) * n[0] + (M[1] - G[1]) * n[1] < 0) n = [-n[0], -n[1]];
            // la longueur DEHORS, contre le milieu de son côté (dedans, on ne savait plus à quel côté elle était : vu en PNG)
            const d = 5 + Math.abs(n[0]) * (largeurTexte(texte) / 2) + Math.abs(n[1]) * 6.5;
            return { n, c: [M[0] + n[0] * d, M[1] + n[1] * d], w: largeurTexte(texte), texte, dy: 4.5, attrs: `font-size="12" font-weight="600" fill="${COULEUR_COTE}" data-mesure="cote${i}"` };
        });
        const mesuresAngles = angles.map(({ i, texte }) => {
            const S = A[i]; const u = unitaire(vec(S, A[(i + A.length - 1) % A.length])); const v = unitaire(vec(S, A[(i + 1) % A.length]));
            const bis = unitaire([u[0] + v[0], u[1] + v[1]]);
            return { S, c: [S[0] + bis[0] * 30, S[1] + bis[1] * 30], w: largeurTexte(texte), texte, dy: 4.5, attrs: `font-size="12" font-weight="600" fill="${COULEUR_COTE}" data-mesure="angle${i}"` };
        });
        // deux mesures d'angle qui se chevauchent (sommets voisins, côté court : « 102° » sur « 116° », vu en PNG)
        // glissent chacune vers SON sommet, le long de la droite qui joint les deux sommets : chacune reste à son angle
        for (let tour = 0; tour < 40; tour++) {
            let bouge = false;
            mesuresAngles.forEach((m1, a) => mesuresAngles.slice(a + 1).forEach((m2) => {
                const chevauche = Math.abs(m2.c[0] - m1.c[0]) < (m1.w + m2.w) / 2 + 3 && Math.abs(m2.c[1] - m1.c[1]) < 15;
                if (!chevauche) return;
                const u = unitaire(vec(m1.S, m2.S));
                m1.c = [m1.c[0] - u[0] * 1.5, m1.c[1] - u[1] * 1.5]; m2.c = [m2.c[0] + u[0] * 1.5, m2.c[1] + u[1] * 1.5];
                bouge = true;
            }));
            if (!bouge) break;
        }
        const nomsSommets = [];
        const nom = (P, g, texte) => { const d = unitaire(vec(g, P)); nomsSommets.push({ c: [P[0] + d[0] * 14, P[1] + d[1] * 14], w: largeurTexte(texte), texte, dy: 5, attrs: 'font-size="13" font-weight="700" fill="#1e293b"' }); };
        A.forEach((P, i) => nom(P, G, noms[i]));
        B.forEach((P, i) => nom(P, G2, nomsImage[i]));
        // une longueur qui touche le nom d'un sommet (petit côté d'une figure mince : « 2 cm » sur « P », vu au test de mesure)
        // s'éloigne de son côté, tout droit, jusqu'à ce qu'elle ne touche plus rien
        const touche = (e, f) => Math.abs(e.c[0] - f.c[0]) < (e.w + f.w) / 2 + 1 && Math.abs(e.c[1] - f.c[1]) < 13;
        longueurs.forEach((l) => {
            for (let pas = 0; pas < 15 && nomsSommets.some((m) => touche(l, m)); pas++) l.c = [l.c[0] + l.n[0] * 2, l.c[1] + l.n[1] * 2];
        });
        ecritures.push(...longueurs, ...mesuresAngles, ...nomsSommets);
        const debord = Math.max(0, ...ecritures.map((e) => Math.max(4 - (e.c[0] - e.w / 2), e.c[0] + e.w / 2 - (W - 4), 4 - (e.c[1] - 7), e.c[1] + 7 - (H - 4))));
        return { A, B, ecritures, debord };
    };
    let marge = 30; let place = placer(marge);
    for (let essai = 0; essai < 8 && place.debord > 0.5; essai++) { marge += place.debord + 1; place = placer(marge); }
    const { A, B, ecritures } = place;
    const polygone = (T, fill) => `<polygon points="${T.map((p) => `${f1(p[0])},${f1(p[1])}`).join(' ')}" fill="${fill}" stroke="${TRAIT}" stroke-width="1.8" stroke-linejoin="round"/>`;
    let s = polygone(A, VERT) + polygone(B, ORANGE);
    angles.forEach(({ i }) => {
        const S = A[i]; const u = unitaire(vec(S, A[(i + A.length - 1) % A.length])); const v = unitaire(vec(S, A[(i + 1) % A.length]));
        const r = 14; const a0 = [S[0] + u[0] * r, S[1] + u[1] * r]; const b0 = [S[0] + v[0] * r, S[1] + v[1] * r];
        const sweep = u[0] * v[1] - u[1] * v[0] > 0 ? 1 : 0;
        s += `<path d="M ${f1(a0[0])} ${f1(a0[1])} A ${r} ${r} 0 0 ${sweep} ${f1(b0[0])} ${f1(b0[1])}" fill="none" stroke="#111827" stroke-width="1.4" data-code="angle"/>`;
    });
    // droits : l'angle droit codé aux sommets donnés de la figure de départ (un carré de 10 px)
    droits.forEach((i) => {
        const S = A[i]; const u = unitaire(vec(S, A[(i + A.length - 1) % A.length])); const v = unitaire(vec(S, A[(i + 1) % A.length]));
        const k = 10;
        s += `<polyline points="${f1(S[0] + u[0] * k)},${f1(S[1] + u[1] * k)} ${f1(S[0] + (u[0] + v[0]) * k)},${f1(S[1] + (u[1] + v[1]) * k)} ${f1(S[0] + v[0] * k)},${f1(S[1] + v[1] * k)}" fill="none" stroke="#111827" stroke-width="1.6" data-code="carre" data-sommet="${noms[i]}"/>`;
    });
    ecritures.forEach((e) => { s += `<text x="${f1(e.c[0])}" y="${f1(e.c[1] + e.dy)}" ${e.attrs} text-anchor="middle">${e.texte}</text>`; });
    const fig = [...poly.map((p, i) => `${noms[i]}:${p[0].toFixed(6)},${p[1].toFixed(6)}`), ...image.map((p, i) => `${nomsImage[i]}:${p[0].toFixed(6)},${p[1].toFixed(6)}`)].join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-figure="${fig}" data-depart="${noms.join('')}" role="img" aria-label="${aria}">${s}</svg>`;
};

/**
 * Des points et des FLÈCHES de translation (190 « La bonne propriété ») : « la
 * translation qui transforme A en B transforme C en D » (deux flèches de même
 * vecteur), ou une droite et son image (droites prolongées, une flèche).
 * points : { A: [x, y] } en cm (y vers le haut) ; fleches : [['A', 'B']] (de A
 * vers B) ; droites : [['A', 'B']] (tracées au-delà des deux points). Chaque
 * nom se place dans la direction la plus éloignée de tous les traits qui
 * partent de son point.
 */
export const svgFleches = ({ points, fleches = [], droites = [], aria }) => {
    const noms = Object.keys(points);
    const bouts = droites.map(([a, b]) => {
        const P = points[a]; const Q = points[b]; const u = vec(P, Q);
        return [[P[0] - 0.35 * u[0], P[1] - 0.35 * u[1]], [Q[0] + 0.35 * u[0], Q[1] + 0.35 * u[1]]];
    });
    const tous = [...noms.map((n) => points[n]), ...bouts.flat()];
    const xs = tous.map((p) => p[0]); const ys = tous.map((p) => p[1]);
    const marge = 30;
    const ech = Math.min((W - 2 * marge) / (Math.max(...xs) - Math.min(...xs) || 1), (H - 2 * marge) / (Math.max(...ys) - Math.min(...ys) || 1));
    const x0 = (W - (Math.max(...xs) - Math.min(...xs)) * ech) / 2; const y0 = (H - (Math.max(...ys) - Math.min(...ys)) * ech) / 2;
    const px = (p) => [x0 + (p[0] - Math.min(...xs)) * ech, y0 + (Math.max(...ys) - p[1]) * ech];
    const Pn = {}; noms.forEach((n) => { Pn[n] = px(points[n]); });
    let s = '';
    bouts.forEach(([P, Q]) => { const a = px(P); const b = px(Q); s += `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${TRAIT}" stroke-width="1.8" data-code="droite"/>`; });
    fleches.forEach(([a, b]) => {
        const P = Pn[a]; const Q = Pn[b]; const u = unitaire(vec(P, Q)); const n = perp(u);
        const base = [Q[0] - u[0] * 11, Q[1] - u[1] * 11];
        s += `<line x1="${f1(P[0])}" y1="${f1(P[1])}" x2="${f1(base[0])}" y2="${f1(base[1])}" stroke="#EA580C" stroke-width="2.2" data-code="fleche" data-de="${a}" data-vers="${b}"/>`;
        s += `<polygon points="${f1(Q[0])},${f1(Q[1])} ${f1(base[0] + n[0] * 5)},${f1(base[1] + n[1] * 5)} ${f1(base[0] - n[0] * 5)},${f1(base[1] - n[1] * 5)}" fill="#EA580C"/>`;
    });
    // les directions interdites de chaque point : vers l'autre bout de ses flèches, dans les deux sens de ses droites
    const interdites = {}; noms.forEach((n) => { interdites[n] = []; });
    fleches.forEach(([a, b]) => { interdites[a].push(unitaire(vec(Pn[a], Pn[b]))); interdites[b].push(unitaire(vec(Pn[b], Pn[a]))); });
    droites.forEach(([a, b]) => { const u = unitaire(vec(Pn[a], Pn[b])); [a, b].forEach((n) => { interdites[n].push(u, [-u[0], -u[1]]); }); });
    noms.forEach((n) => {
        const P = Pn[n];
        let meilleure = [0, -1]; let ecart = -Infinity;
        for (let k = 0; k < 16; k++) {
            const t = (k * Math.PI) / 8; const d = [Math.cos(t), Math.sin(t)];
            const e = Math.min(Math.PI, ...interdites[n].map((u) => Math.acos(Math.max(-1, Math.min(1, u[0] * d[0] + u[1] * d[1])))));
            if (e > ecart + 1e-9) { ecart = e; meilleure = d; }
        }
        s += `<circle cx="${f1(P[0])}" cy="${f1(P[1])}" r="2.6" fill="#1e293b"/>`;
        s += `<text x="${f1(P[0] + meilleure[0] * 15)}" y="${f1(P[1] + meilleure[1] * 15 + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${n}</text>`;
    });
    const fig = noms.map((n) => `${n}:${points[n][0].toFixed(6)},${points[n][1].toFixed(6)}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-figure="${fig}" role="img" aria-label="${aria}">${s}</svg>`;
};

// ═══ La figure nommée et son image (187 « L'image d'un sommet », 189 « La figure image ») ═══

/** Les lettres des noms (celles de 189 : ni I, ni O, ni Q, ni W à Z). */
export const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUV'.split('');

/**
 * Le quadrilatère de départ (sommets 0..3, nommés dans l'ordre), l'image de
 * chaque sommet, et le NOM de l'image lu à partir d'un autre sommet, dans un
 * sens ou dans l'autre (RAMI → BELO du prof : l'autre sens) : la
 * correspondance ne se lit pas dans l'ordre des lettres. ordreImage[j] : le
 * sommet de départ dont l'image est à la place j du nom de l'image.
 * Partagé depuis le 2026-09-25 par 187 (« L'image d'un sommet », venue de
 * 189) et 189 (« La figure image »).
 */
export const tirerImage = () => {
    const q = quadrilatere();
    const lettres = pickLettresSains(POOL_LETTRES, 8);
    const noms = lettres.slice(0, 4); const nomsImage = lettres.slice(4, 8);
    const inverse = randInt(0, 1) === 0;
    const k = inverse ? randInt(0, 3) : randInt(1, 3);                       // dans le même sens, jamais à partir du même sommet
    const ordreImage = [0, 1, 2, 3].map((j) => (inverse ? (k - j + 4) % 4 : (j + k) % 4));
    return { ...q, noms, nomsImage, ordreImage, nomImage: ordreImage.map((i) => nomsImage[i]).join(''), nom: noms.join(''), vecteur: vecteurCoteACote(q.poly) };
};

/** La phrase d'entrée : « Le quadrilatère CRAV est l'image du quadrilatère JNHB par une translation. » */
export const phraseImage = (t) => `<p>Le quadrilatère ${tex(t.nomImage)} est l'image du quadrilatère ${tex(t.nom)} par une translation.</p>`;

/** La figure (verte) et son image (orange), avec les mesures demandées écrites sur la figure de départ. */
export const figureImage = (t, cotes, angles) => imgElement(svgTranslation({
    poly: t.poly, noms: t.noms, nomsImage: t.nomsImage, vecteur: t.vecteur, cotes, angles,
    aria: `Le quadrilatère ${t.nom} et son image ${t.nomImage} par une translation.`,
}), 'large');
